import { useState } from 'react';
import { useSession } from '../lib/session/sessionContext';
import { Icon, PersonAvatar } from '../ui';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/shadcn/dropdown-menu';
import styles from './CurrentUserMenu.module.css';
import { reportError } from './telemetry';

/**
 * The §6 current-user menu in the sidebar footer. It absorbs and replaces F5-a's interim top-level
 * sign-out (F5-b spec FR-6 / AC-6), so exactly one sign-out control ships: the *Sign out* item here.
 *
 * Shell-local chrome in `src/app` (not a `src/ui` component) because it composes the menu with
 * `useSession()` — a `src/ui` atom may not depend on the session store (F5-b WI-1.2 plan Decision 3).
 * The avatar + name + role identity block is the menu's trigger, opening the menu upward
 * (design-system Rev 74 §6 *Current-user menu*). Sentence case on the items (§9).
 *
 * **Built from shadcn/ui** (AD #39; F20-i spec FR-19): the shadcn `DropdownMenu` (Radix; not modal, a
 * disabled item `aria-disabled` and still reachable), opening with `side="top"`, and the shadcn `Avatar` through
 * `PersonAvatar`.
 *
 * **Items:** *Sign out* only. The mailbox settings are reached from the Email screen's settings button, not
 * from here (F20-i FR-19, Rev 4; design-system Rev 88).
 */

/** The *Sign out* item's accessible name while a sign-out is waiting on the server (BUG-052, §9). */
const SIGNING_OUT_NAME = 'Sign out. Sign-out is in progress';

export interface CurrentUserMenuProps {
    /**
     * Icon-only rail (`AppLayout` mode === 'icon'): render a centred avatar only, hiding the name and
     * role so the trigger fits the narrow collapsed rail instead of overflowing it (BUG-003). The
     * avatar keeps the full display name as its accessible name, so the trigger stays identifiable.
     */
    readonly collapsed?: boolean;
}

/** Shown when the server did not confirm the sign-out, so the user is still signed in (BUG-052). */
const SIGN_OUT_FAILED_MESSAGE = "Sign-out didn't complete. Try again.";

export function CurrentUserMenu({ collapsed = false }: CurrentUserMenuProps) {
    const session = useSession();
    // Above the early return below: a hook may not sit behind a condition.
    const [signOutFailed, setSignOutFailed] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    // Only mounted inside the authenticated frame; a non-authenticated status means the gate is
    // already redirecting, so render nothing rather than a menu with no user behind it.
    if (session.status !== 'authenticated') {
        return null;
    }

    const { user, roleName, signOut } = session;

    function handleSignOut(): void {
        // The disabled item already refuses the press; the state check keeps the guard in one place.
        if (signingOut) {
            return;
        }
        setSignOutFailed(false);
        setSigningOut(true);
        signOut().then(
            () => setSigningOut(false),
            (error: unknown) => {
                reportError({ error, where: 'sign-out' });
                setSigningOut(false);
                setSignOutFailed(true);
            },
        );
    }

    // The shadcn Avatar through `PersonAvatar`: the full name as its accessible name, the initials hidden.
    const avatar = <PersonAvatar name={user.displayName} />;

    // The block's content is the trigger's accessible name (WCAG 2.5.3 Label in Name). Collapsed, only
    // the avatar shows, and its accessible name is the full display name. Expanded, the visible name and
    // role make the trigger's name, so the avatar beside them is hidden from the accessibility tree
    // rather than read as the name twice. The space between the name and role keeps the two words apart
    // in the accessible name.
    const identity = (
        <span className={styles.identity} data-collapsed={collapsed ? 'true' : undefined}>
            {collapsed ? (
                avatar
            ) : (
                <>
                    <span aria-hidden="true" className={styles.avatar}>
                        {avatar}
                    </span>
                    <span className={styles.text}>
                        <span className={styles.name}>{user.displayName}</span>{' '}
                        <span className={styles.role}>{roleName}</span>
                    </span>
                    {/* Decorative cue that the identity block opens a menu (F5-b finding F-002): a
                        trailing up/down chevron, aria-hidden, so it adds no announcement. */}
                    <span className={styles.chevron} data-testid="app-user-menu-chevron" aria-hidden="true">
                        <Icon name="expand" size="sm" />
                    </span>
                </>
            )}
        </span>
    );

    return (
        <div className={styles.region} data-collapsed={collapsed ? 'true' : undefined}>
            {/* The sign-out failure (BUG-052). `signOut` rejects when the server did not confirm the
                logout, and the session stays signed in, so the user must be told the click did
                nothing. The live region is mounted for the menu's whole life and only its content
                changes, so it is in the accessibility tree before it has anything to say; empty, it has
                no box. A plain polite region, no `alert` role: the footer is on every signed-in page.
                Polite, because the message arrives after the menu has closed and focus is back on its
                trigger. The `danger` tone, like the login page's failure message. */}
            <p
                aria-atomic="true"
                aria-live="polite"
                className={styles.signOutError}
                data-testid="app-user-menu-sign-out-error"
            >
                {signOutFailed ? SIGN_OUT_FAILED_MESSAGE : null}
            </p>
            <DropdownMenu>
                <DropdownMenuTrigger
                    className={styles.trigger}
                    data-full-width={collapsed ? undefined : 'true'}
                    data-testid="app-user-menu"
                >
                    {identity}
                </DropdownMenuTrigger>
                {/* Upward from the footer (Rev 74), its animation running the same way (the content's
                    `data-[side=top]` slide); at least as wide as the chip it opens from, by the CSS Module,
                    because `src/app` is not a Tailwind `@source` and a utility here is never generated. */}
                <DropdownMenuContent
                    align="start"
                    className={styles.menu}
                    data-testid="app-user-menu-menu"
                    side="top"
                >
                    <DropdownMenuItem
                        // §9's permission rule: the reason is in the accessible name, so a screen-reader
                        // user hears why the item is unavailable when they land on it (BUG-052).
                        aria-label={signingOut ? SIGNING_OUT_NAME : undefined}
                        data-testid="app-user-menu-sign-out"
                        disabled={signingOut}
                        onSelect={handleSignOut}
                    >
                        <Icon name="signout" size="sm" />
                        Sign out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
